/**
 * stockDefaultTemplate — the STOCK fallback report template.
 *
 * Used as the terminal default when a report resolves with:
 *   - NO assigned `templateId`, AND
 *   - NO account/org default template (`isDefault`), AND
 *   - NO archetype context (no archetype seed to fall back to).
 *
 * Before this existed, that terminal case resolved to a `null` template,
 * which collapsed to the bare legacy `DEFAULT_THEME` (a hero-image cover and
 * the legacy palette). The product default is now the curated STOCK look:
 * the academic-editorial WHITE `house` theme preset (Fraunces display serif +
 * Newsreader reading body + Hanken Grotesk apparatus, pure-white page, single
 * teal accent) with the `editorial-masthead` cover layout — and ZERO other
 * overrides, so the unmodified preset shows through everywhere.
 *
 * Resolution mechanics (why a preset id + an explicit coverLayout is enough):
 *   - `themePreset: 'house'` makes `resolveEffectiveTheme` fold the house
 *     palette + typography UNDER any store/template/report overrides. Because
 *     this seed carries NO `theme` partial, the unmodified preset is the
 *     effective look.
 *   - We set `coverLayout: 'editorial-masthead'` explicitly so the
 *     template-editor field (step 4 in `resolveEffectiveTheme` / the
 *     `template?.coverLayout` precedence in `resolveCoverLayout`) wins over any
 *     preset default — yielding the house EditorialMastheadCover.
 *
 * This is a read-only singleton (like the archetype seeds). It is NEVER
 * persisted, NEVER mutated, and NEVER offered for selection — it is purely
 * the resolution-time fallback. A real assigned template OR an account/org
 * default ALWAYS wins (they are consulted earlier in
 * `resolveActiveReportTemplate`).
 */
import type { ReportTemplate, SectionConfig } from '../types/reportTemplate';
import { getDefaultSectionConfigs } from '../config/reportSections';

/** The stock theme preset id — the academic-editorial WHITE house style
 *  (Fraunces display serif + Newsreader reading body + Hanken Grotesk apparatus
 *  on a pure-white page, single teal accent). Was 'memo' (humanist sans, GC-W1);
 *  switched to 'house' so a no-custom-template report renders the signed-off
 *  house style by default. */
export const STOCK_DEFAULT_THEME_PRESET = 'house' as const;

/** The stock cover layout — the editorial-masthead title page (the house cover:
 *  masthead rule, serif display title, standfirst abstract, 2-column numbered
 *  TOC, centred brand logo). Was 'memo' (MemoCover); switched in lock-step with
 *  the `house` preset so a no-custom-template report renders the house cover by
 *  default. */
export const STOCK_DEFAULT_COVER_LAYOUT = 'editorial-masthead' as const;

const STOCK_NOW = '2026-06-16T00:00:00.000Z';

/**
 * The stock fallback template. `scope: 'user'` is cosmetic — this template
 * is never listed or persisted; it only ever flows through
 * `resolveActiveReportTemplate` as the terminal fallback.
 */
export const STOCK_DEFAULT_TEMPLATE: ReportTemplate = {
  id: 'stock-default-house-editorial',
  name: 'Stock default (House · academic-editorial white)',
  scope: 'user',
  // Canonical section configs — same seed a fresh blank template gets, so the
  // body renders the standard section set with no per-section overrides. Deep-
  // frozen: this is an exported singleton, so an accidental in-place mutation
  // (push/splice or `sections[0].visible = false`) would corrupt it session-wide.
  // Consumers must spread-and-replace (`{ ...STOCK, sections: next }`), never mutate.
  sections: getDefaultSectionConfigs().map((s) => Object.freeze({ ...s }) as SectionConfig),
  headingOverrides: {},
  isDefault: false,
  // The curated house preset (palette + typography). No `theme`
  // partial ⇒ the UNMODIFIED preset is the effective look (zero overrides).
  themePreset: STOCK_DEFAULT_THEME_PRESET,
  // House editorial-masthead cover — set explicitly so it beats the preset's own
  // default cover (it matches the house preset's `editorial-masthead` cover
  // anyway; explicit for clarity AND so the cover resolves as an EXPLICIT
  // template choice — bypassing the VITE_EDITORIAL_LAYOUTS default-floor gate in
  // resolveCoverLayout, so the stock cover is house-editorial regardless of flag).
  coverLayout: STOCK_DEFAULT_COVER_LAYOUT,
  createdAt: STOCK_NOW,
  updatedAt: STOCK_NOW,
};
// Freeze the sections ARRAY too (the deep-freeze above covers the elements), so
// neither the array nor its members can be mutated in place.
Object.freeze(STOCK_DEFAULT_TEMPLATE.sections);
